import { useLoaderData, useNavigate, useSearch } from "@tanstack/react-router";
import { ArrowLeft, ChevronRight, ExternalLink, Search, X } from "lucide-react";
import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  type DrillDownDeal,
  formatCurrency,
  formatDate,
  formatNumber,
  formatPercent,
  getPipelineName,
  getStageName,
  METRIC_LABELS,
  type MetricType,
  type Pipeline,
} from "./shared";

interface MetricSummary {
  opportunities: number;
  closedDeals: number;
  errorCount: number;
  grossSales: number;
  netSales: number;
  errorRate: number;
  closeRate: number;
}

interface DetailData {
  metricType: MetricType;
  ownerName: string;
  summary: MetricSummary;
  deals?: DrillDownDeal[];
  opportunityDeals?: DrillDownDeal[];
  closedDealRows?: DrillDownDeal[];
}

export function DealMetricsDetailPage() {
  const { detail, pipelines, propertyLabels } = useLoaderData({
    from: "/_authenticated/deal-metrics/detail",
  }) as { detail: DetailData; pipelines: Pipeline[]; propertyLabels: Record<string, string> };
  const search = useSearch({ from: "/_authenticated/deal-metrics/detail" }) as Record<
    string,
    string | undefined
  >;
  const navigate = useNavigate();

  const [selectedDeal, setSelectedDeal] = useState<DrillDownDeal | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  const metricLabel = METRIC_LABELS[detail.metricType] ?? detail.metricType;

  const filterDeals = (deals: DrillDownDeal[]) => {
    if (!searchQuery.trim()) return deals;
    const q = searchQuery.toLowerCase();
    return deals.filter((d) => (d.dealname ?? "").toLowerCase().includes(q));
  };

  function navigateToMetric(metric: MetricType) {
    navigate({
      to: "/deal-metrics/detail",
      search: { ...search, metric } as any,
    });
  }

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-1.5 text-sm">
        <Button
          variant="ghost"
          size="sm"
          className="gap-1.5 text-muted-foreground hover:text-foreground -ml-2 h-8"
          onClick={() => navigate({ to: "/deal-metrics" })}
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Deal Metrics
        </Button>
        <ChevronRight className="h-3.5 w-3.5 text-muted-foreground/50" />
        <span className="font-medium text-foreground">
          {metricLabel} — {detail.ownerName}
        </span>
      </nav>

      {/* Formula Card */}
      <FormulaCard detail={detail} onNavigateMetric={navigateToMetric} />

      {/* Filter Context */}
      <p className="text-xs text-muted-foreground tracking-wide">
        {search.startDate && search.endDate
          ? `${formatDate(search.startDate)} – ${formatDate(search.endDate)}`
          : "All dates"}
        {detail.ownerName !== "All Consultants" && <span> &middot; {detail.ownerName}</span>}
      </p>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search deals..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-9 pr-8"
        />
        {searchQuery && (
          <button
            type="button"
            onClick={() => setSearchQuery("")}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Deal Table(s) */}
      {detail.metricType === "closeRate" ? (
        <CloseRateDetail
          opportunityDeals={filterDeals(detail.opportunityDeals ?? [])}
          closedDealRows={filterDeals(detail.closedDealRows ?? [])}
          pipelines={pipelines}
          onSelectDeal={setSelectedDeal}
        />
      ) : (
        <DealTable
          deals={filterDeals(detail.deals ?? [])}
          metricType={detail.metricType}
          pipelines={pipelines}
          onSelectDeal={setSelectedDeal}
        />
      )}

      {/* Deal Detail Drawer */}
      <DealDetailSheet
        deal={selectedDeal}
        pipelines={pipelines}
        propertyLabels={propertyLabels}
        onClose={() => setSelectedDeal(null)}
      />
    </div>
  );
}

/* ── Formula Card ──────────────────────────────────────────────────── */

function FormulaCard({
  detail,
  onNavigateMetric,
}: {
  detail: DetailData;
  onNavigateMetric: (metric: MetricType) => void;
}) {
  const { summary, metricType } = detail;

  return (
    <div className="relative overflow-hidden rounded-xl border border-[oklch(0.73_0.11_70/0.3)] bg-gradient-to-br from-[oklch(0.28_0.06_240/0.04)] to-[oklch(0.73_0.11_70/0.06)]">
      {/* Decorative accent line */}
      <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[oklch(0.73_0.11_70)] to-transparent" />

      <div className="px-6 py-5">
        <p className="text-xs font-medium uppercase tracking-[0.15em] text-muted-foreground mb-3">
          Calculation
        </p>

        <div className="font-serif text-lg leading-relaxed text-foreground">
          {metricType === "opportunities" && (
            <span>
              <Strong>{formatNumber(summary.opportunities)}</Strong> deals where quote form was
              filled (Sales + Production pipelines)
            </span>
          )}

          {metricType === "closedDeals" && (
            <span>
              <Strong>{formatNumber(summary.closedDeals)}</Strong> deals with order placed
              (Production pipeline)
            </span>
          )}

          {metricType === "grossSales" && (
            <span>
              SUM of amount_in_home_currency across{" "}
              <Strong>{formatNumber(summary.closedDeals)}</Strong> closed deals ={" "}
              <Strong>{formatCurrency(summary.grossSales)}</Strong>
            </span>
          )}

          {metricType === "netSales" && (
            <span>
              SUM of net_price across <Strong>{formatNumber(summary.closedDeals)}</Strong> closed
              deals = <Strong>{formatCurrency(summary.netSales)}</Strong>
            </span>
          )}

          {metricType === "errors" && (
            <span>
              <Strong>{formatNumber(summary.errorCount)}</Strong> consultant errors across{" "}
              <Strong>{formatNumber(summary.closedDeals)}</Strong> closed deals ={" "}
              <Strong>{formatPercent(summary.errorRate)}</Strong> error rate
            </span>
          )}

          {metricType === "closeRate" && (
            <span>
              <MetricLink onClick={() => onNavigateMetric("closedDeals")}>
                {formatNumber(summary.closedDeals)} closed deals
              </MetricLink>{" "}
              /{" "}
              <MetricLink onClick={() => onNavigateMetric("opportunities")}>
                {formatNumber(summary.opportunities)} opportunities
              </MetricLink>{" "}
              = <Strong>{formatPercent(summary.closeRate)}</Strong>
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function Strong({ children }: { children: React.ReactNode }) {
  return (
    <span className="font-bold text-[oklch(0.28_0.06_240)] dark:text-[oklch(0.73_0.11_70)]">
      {children}
    </span>
  );
}

function MetricLink({ children, onClick }: { children: React.ReactNode; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="font-bold text-[oklch(0.73_0.11_70)] underline underline-offset-2 decoration-[oklch(0.73_0.11_70/0.4)] hover:decoration-[oklch(0.73_0.11_70)] transition-colors cursor-pointer"
    >
      {children}
    </button>
  );
}

/* ── Close Rate Detail (split view) ────────────────────────────────── */

function CloseRateDetail({
  opportunityDeals,
  closedDealRows,
  pipelines,
  onSelectDeal,
}: {
  opportunityDeals: DrillDownDeal[];
  closedDealRows: DrillDownDeal[];
  pipelines: Pipeline[];
  onSelectDeal: (deal: DrillDownDeal) => void;
}) {
  return (
    <div className="space-y-6">
      <div>
        <h4 className="font-serif text-sm font-semibold mb-2">
          Opportunities ({opportunityDeals.length})
        </h4>
        <DealTable
          deals={opportunityDeals}
          metricType="opportunities"
          pipelines={pipelines}
          onSelectDeal={onSelectDeal}
        />
      </div>
      <Separator />
      <div>
        <h4 className="font-serif text-sm font-semibold mb-2">
          Closed Deals ({closedDealRows.length})
        </h4>
        <DealTable
          deals={closedDealRows}
          metricType="closedDeals"
          pipelines={pipelines}
          onSelectDeal={onSelectDeal}
        />
      </div>
    </div>
  );
}

/* ── Deal Table ────────────────────────────────────────────────────── */

function DealTable({
  deals,
  metricType,
  pipelines,
  onSelectDeal,
}: {
  deals: DrillDownDeal[];
  metricType: MetricType;
  pipelines: Pipeline[];
  onSelectDeal: (deal: DrillDownDeal) => void;
}) {
  if (deals.length === 0) {
    return <p className="text-sm text-muted-foreground text-center py-8">No deals found.</p>;
  }

  const dateLabel = metricType === "opportunities" ? "Quote Filled" : "Order Date";
  const getDate = (d: DrillDownDeal) =>
    metricType === "opportunities" ? d.free_quote_form_filled : d.order_date;

  const showFinancials = ["closedDeals", "grossSales", "netSales", "errors"].includes(metricType);
  const showErrors = metricType === "errors";

  const totalGross = showFinancials
    ? deals.reduce((sum, d) => sum + (d.amount_in_home_currency ?? 0), 0)
    : 0;
  const totalNet = showFinancials ? deals.reduce((sum, d) => sum + (d.net_price ?? 0), 0) : 0;

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow className="bg-muted/30">
            <TableHead className="text-xs uppercase tracking-wider">Deal</TableHead>
            <TableHead className="text-xs uppercase tracking-wider">{dateLabel}</TableHead>
            <TableHead className="text-xs uppercase tracking-wider">Stage</TableHead>
            {showFinancials && (
              <>
                <TableHead className="text-xs uppercase tracking-wider text-right">Gross</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-right">Net</TableHead>
              </>
            )}
            {showErrors && (
              <>
                <TableHead className="text-xs uppercase tracking-wider">Error Type</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Attribution</TableHead>
              </>
            )}
            <TableHead className="text-xs uppercase tracking-wider">Owner</TableHead>
            <TableHead className="text-xs uppercase tracking-wider text-right">HS ID</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {deals.map((d) => (
            <TableRow
              key={`${d.hubspot_deal_id}-${d.metric_source ?? ""}`}
              className="cursor-pointer"
              onClick={() => onSelectDeal(d)}
            >
              <TableCell className="max-w-[180px]">
                <span className="font-medium truncate block">{d.dealname ?? "Untitled"}</span>
              </TableCell>
              <TableCell className="text-sm text-muted-foreground">
                {formatDate(getDate(d))}
              </TableCell>
              <TableCell>
                <Badge variant="outline" className="text-xs">
                  {getStageName(d.dealstage, pipelines)}
                </Badge>
              </TableCell>
              {showFinancials && (
                <>
                  <TableCell className="text-right tabular-nums">
                    {formatCurrency(d.amount_in_home_currency)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatCurrency(d.net_price)}
                  </TableCell>
                </>
              )}
              {showErrors && (
                <>
                  <TableCell className="text-sm">{d.type_of_error ?? "—"}</TableCell>
                  <TableCell className="text-sm">{d.accuracy_error ?? "—"}</TableCell>
                </>
              )}
              <TableCell className="text-sm text-muted-foreground">{d.owner_name ?? "—"}</TableCell>
              <TableCell className="text-right">
                <a
                  href={`https://app.hubspot.com/contacts/${d.hubspot_deal_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 font-mono text-xs text-primary hover:underline"
                >
                  {d.hubspot_deal_id}
                  <ExternalLink className="h-3 w-3" />
                </a>
              </TableCell>
            </TableRow>
          ))}

          {showFinancials && deals.length > 1 && (
            <TableRow className="bg-muted/20 font-semibold border-t-2">
              <TableCell>{deals.length} deals</TableCell>
              <TableCell />
              <TableCell />
              <TableCell className="text-right tabular-nums">
                {formatCurrency(totalGross)}
              </TableCell>
              <TableCell className="text-right tabular-nums">{formatCurrency(totalNet)}</TableCell>
              {showErrors && (
                <>
                  <TableCell />
                  <TableCell />
                </>
              )}
              <TableCell />
              <TableCell />
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}

function DealField({
  label,
  hsProperty,
  children,
}: {
  label: string;
  hsProperty: string;
  children: React.ReactNode;
}) {
  return (
    <>
      <dt>
        <span className="text-muted-foreground">{label}</span>
        <span className="block font-mono text-[10px] text-muted-foreground/50">{hsProperty}</span>
      </dt>
      <dd>{children}</dd>
    </>
  );
}

/* ── Deal Detail Sheet ─────────────────────────────────────────────── */

function DealDetailSheet({
  deal,
  pipelines,
  propertyLabels,
  onClose,
}: {
  deal: DrillDownDeal | null;
  pipelines: Pipeline[];
  propertyLabels: Record<string, string>;
  onClose: () => void;
}) {
  const label = (hsName: string, fallback: string) => propertyLabels[hsName] ?? fallback;
  return (
    <Sheet open={!!deal} onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="overflow-y-auto sm:max-w-lg">
        {deal && (
          <>
            <SheetHeader>
              <SheetTitle className="text-lg leading-snug">
                {deal.dealname ?? "Untitled Deal"}
              </SheetTitle>
              <div className="flex items-center gap-2 pt-1">
                <Badge variant="outline" className="text-xs">
                  {getPipelineName(deal.pipeline, pipelines)}
                </Badge>
                <span className="text-xs text-muted-foreground">
                  {getStageName(deal.dealstage, pipelines)}
                </span>
              </div>
            </SheetHeader>

            <div className="mt-6 space-y-6">
              <div>
                <h4 className="mb-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Deal Info
                </h4>
                <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
                  <DealField label={label("amount", "Amount")} hsProperty="amount">
                    <span className="font-medium tabular-nums">{formatCurrency(deal.amount)}</span>
                  </DealField>
                  <DealField
                    label={label("amount_in_home_currency", "Amount (Home)")}
                    hsProperty="amount_in_home_currency"
                  >
                    <span className="font-medium tabular-nums">
                      {formatCurrency(deal.amount_in_home_currency)}
                    </span>
                  </DealField>
                  <DealField label={label("net_price", "Net Price")} hsProperty="net_price">
                    <span className="font-medium tabular-nums">
                      {formatCurrency(deal.net_price)}
                    </span>
                  </DealField>
                  <DealField
                    label={label("hubspot_owner_id", "Owner")}
                    hsProperty="hubspot_owner_id"
                  >
                    {deal.owner_name ?? "—"}
                  </DealField>
                  <DealField label={label("closedate", "Close Date")} hsProperty="closedate">
                    {formatDate(deal.closedate)}
                  </DealField>
                  <DealField label={label("order_date", "Order Date")} hsProperty="order_date">
                    {formatDate(deal.order_date)}
                  </DealField>
                  <DealField
                    label={label("free_quote_form_filled", "Quote Form Filled")}
                    hsProperty="free_quote_form_filled"
                  >
                    {formatDate(deal.free_quote_form_filled)}
                  </DealField>
                  <DealField
                    label={label("type_of_error", "Error Type")}
                    hsProperty="type_of_error"
                  >
                    {deal.type_of_error ?? "—"}
                  </DealField>
                  <DealField
                    label={label("accuracy_error", "Accuracy Error")}
                    hsProperty="accuracy_error"
                  >
                    {deal.accuracy_error ?? "—"}
                  </DealField>
                  <DealField label={label("error_notes", "Error Notes")} hsProperty="error_notes">
                    {deal.error_notes ?? "—"}
                  </DealField>
                  <DealField label={label("createdate", "Created")} hsProperty="createdate">
                    {formatDate(deal.hubspot_createdate)}
                  </DealField>
                  <DealField
                    label={label("hs_lastmodifieddate", "Last Modified")}
                    hsProperty="hs_lastmodifieddate"
                  >
                    {formatDate(deal.hs_last_modified_date)}
                  </DealField>
                  <DealField label={label("pipeline", "Pipeline")} hsProperty="pipeline">
                    {getPipelineName(deal.pipeline, pipelines)}
                  </DealField>
                  <DealField label={label("dealstage", "Stage")} hsProperty="dealstage">
                    {getStageName(deal.dealstage, pipelines)}
                  </DealField>
                  <dt className="text-muted-foreground">HubSpot ID</dt>
                  <dd>
                    <a
                      href={`https://app.hubspot.com/contacts/${deal.hubspot_deal_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 font-mono text-xs text-primary hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {deal.hubspot_deal_id}
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  </dd>
                </dl>
              </div>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
